import { useQueryClient } from 'react-query';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useHistory } from 'react-router';
import { auth, logout, logoutUser, merchantLogout, validateSession } from '~/api/auth';
import paths from '~/pages/paths';
import { ApiError } from '~/types/ApiError';
import { redirectAfterVerification, redirectToExternalAuth } from '~/utils/redirectToExternalAuth';
import { isAgent, isNogoManager, isSales } from '~/utils/getDecodedJwt';
import { getLocale } from '~/api/locale';
import { allLocalesQueryKey, useGetAllLanguages, useSetLanguage } from '~/hooks/i18nHooks';

const removePreloader = (): void => {
  const ele = document.getElementById('ipl-progress-indicator');
  if (ele) {
    // fade out
    ele.classList.add('available');
    setTimeout(() => {
      // remove from DOM
      ele.outerHTML = '';
    }, 500);
  }
};

export const useInitApp = () => {
  const [isAppReady, setAppReady] = useState(false);
  const { setLanguage, language: i18nLanguage } = useSetLanguage();
  useGetAllLanguages();
  const history = useHistory();
  const queryClient = useQueryClient();

  useEffect(() => {
    const currentPath = window.location.pathname;

    if (
      currentPath === paths.auth.merchantVerify ||
      currentPath === paths.auth.merchantVerifyOption2
    ) {
      console.log('Multi Browsers', currentPath);
      redirectAfterVerification();
      return;
    }
    if (
      currentPath === paths.auth.merchantAuthCallback ||
      currentPath === paths.auth.merchantInviteAuthCallback ||
      currentPath === paths.auth.agentAuthCallback
    ) {
      void queryClient.prefetchQuery(allLocalesQueryKey);
      removePreloader();
      setAppReady(true);
    } else if (auth.getToken()) {
      void (async () => {
        try {
          // await validateSession();
          const backendLocale = await getLocale();
          void queryClient.prefetchQuery(allLocalesQueryKey);

          if (i18nLanguage !== backendLocale) {
            setLanguage(i18nLanguage);
          }
          removePreloader();
          setAppReady(true);
        } catch (err) {
          const error = err as ApiError;
          console.log(`token is bad; redirecting to auth; error:`, error);
          if (error.response?.status === 401) {
            redirectToExternalAuth();
            auth.clearSession();
          }
          if (error.response?.data?.code === 'INVALID_APPLICATION_ID') {
            removePreloader();
            setAppReady(true);
            auth.clearAgentApplicationId();
            history.push(paths.home._);
          }
        }
      })();
    } else {
      console.log(`no token is set; redirecting to auth`);
      redirectToExternalAuth();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return isAppReady;
};

export const useLogout = () => {
  const history = useHistory(); // Correct for react-router v5
  const queryClient = useQueryClient();
  const isLoggingOut = useRef(false); // Prevent duplicate logout calls

  const handleLogout = useCallback(async () => {
    // Guard against concurrent logout calls (e.g. double-click)
    if (isLoggingOut.current) return;
    isLoggingOut.current = true;

    const agent = isAgent();
    const sales = isSales();
    const nogoManager = isNogoManager()
    const session = auth.getSession();

    // For merchant logout: capture the redirect URL returned by the API
    let merchantLogoutUrl: string | undefined;

    try {
      if (agent || sales || nogoManager) {
        try {
          await logoutUser();
        } catch (e: unknown) {
          console.error('Failed to logout user from UserController; error:', e);
        }

        // ✅ Null-check session before accessing its properties
        if (session) {
          try {
            await logout({
              url:
                session.logoutUrl ||
                `https://${window.location.host}/auth/realms/${session.realm}/protocol/openid-connect/logout`,
              data: {
                client_id: session.clientId,
                refresh_token: session.refreshToken,
              },
            });
          } catch (e: unknown) {
            console.error('Failed to logout; error:', e);
          }
        }
      } else {
        try {
          const res = await merchantLogout({
            redirectUrl: `https://${window.location.host}`,
          });
          // Capture the logoutUrl from the response to use after cleanup
          merchantLogoutUrl = res?.logoutUrl;
        } catch (e: unknown) {
          console.error('Failed to logout merchant; error:', e);
        }
      }
    } finally {
      // ✅ Cleanup always runs regardless of errors above
      auth.clearSession();
      queryClient.clear();

      if (agent || sales || nogoManager) {
        auth.clearAgentApplicationId(); // ✅ Scoped to agent/sales only
        history.push('/agent');
      } else {
        // Redirect to the identity provider logoutUrl if returned, otherwise fallback to '/'
        window.location.replace(merchantLogoutUrl ?? '/');
      }

      isLoggingOut.current = false;
    }
  }, [history, queryClient]);

  return { handleLogout };
};
